import cloneDeep from 'lodash/cloneDeep';
import {ObjectUnsubscribedError, Observable, Observer, Subject, Subscriber, Subscription} from 'rxjs';
import {Session} from './session';

export class SessionSubject<T extends Session> extends Subject<T> {
  private session!: T;
  private nextObservable: Observable<T> | undefined;
  private nextSubscription: Subscription | undefined;

  get exists(): boolean {
    return this.session && !this.nextObservable;
  }

  get snapshot(): T {
    return cloneDeep(this.session);
  }

  nextAsync(observable: Observable<T>) {
    if (this.closed) {
      throw new ObjectUnsubscribedError();
    } else if (this.isStopped) {
      return;
    }

    this.unsubscribeFromNextObservable();
    this.nextObservable = observable;

    if (this.observers.length) {
      this.subscribeToNextObservable();
    }
  }

  private unsubscribeFromNextObservable() {
    if (this.nextSubscription) {
      this.nextSubscription.unsubscribe();
      this.nextSubscription = undefined;
    }

    this.nextObservable = undefined;
  }

  private subscribeToNextObservable() {
    this.nextSubscription = this.nextObservable?.subscribe(
      (session) => this.next(session),
      (error) => this.error(error),
      () => {
        this.nextSubscription = undefined;
        this.nextObservable = undefined;
      },
    );
  }

  override error(thrownError: unknown) {
    this.next(undefined);
  }

  override next(session: T | undefined) {
    if (this.closed) {
      throw new ObjectUnsubscribedError();
    }

    this.session = cloneDeep(session) as T;
    this.unsubscribeFromNextObservable();
    this.applyToObservers((observer) => observer.next(session as T));
  }

  private applyToObservers(fn: (observer: Observer<T>) => void) {
    // Copy the observers array so we can iterate all of them without risking it being mutated while iterating it.
    [...this.observers].forEach(fn);
  }

  _subscribe(subscriber: Subscriber<T>): Subscription {
    if (this.closed) {
      throw new ObjectUnsubscribedError();
    } else if (this.isStopped) {
      this.pushToSubscriber(subscriber);
      subscriber.complete();
      return Subscription.EMPTY;
    }

    if (!this.nextObservable) {
      this.pushToSubscriber(subscriber);
    } else if (!this.nextSubscription) {
      // Push before the subscription otherwise it may end before and the subscriber never gets notified
      this.observers.push(subscriber);
      this.subscribeToNextObservable();
    } else {
      this.observers.push(subscriber);
    }

    return Subject.create(this, subscriber);
  }

  private pushToSubscriber(subscriber: Subscriber<T>) {
    subscriber.next(this.snapshot);
  }
}
